/**
 * Generators React Query Hooks
 * Custom hooks for managing generator data with React Query
 */

import { useQuery } from '@tanstack/react-query';
import { generatorsService } from '../api/services/generators.service';
import type {
  GeneratorMapPoint,
  GeneratorMapParams,
  ClusterPoint,
  ClusterParams,
} from '../api/types/generators.types';

/**
 * Query key factory for generator queries
 */
export const generatorsKeys = {
  all: ['generators'] as const,
  map: (params?: GeneratorMapParams) =>
    [...generatorsKeys.all, 'map', params] as const,
  clusters: (params: ClusterParams) =>
    [...generatorsKeys.all, 'clusters', params] as const,
  search: (query: string, params?: GeneratorMapParams) =>
    [...generatorsKeys.all, 'search', query, params] as const,
};

/**
 * Hook to fetch generators for map display
 *
 * @param params - Query parameters (status filter)
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with generator map points
 */
export const useGeneratorsMap = (
  params?: GeneratorMapParams,
  enabled = true
) => {
  return useQuery<GeneratorMapPoint[], Error>({
    queryKey: generatorsKeys.map(params),
    queryFn: () => generatorsService.getGeneratorsMap(params),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch generator clusters for map display
 *
 * @param params - Query parameters (zoom, status, bounds)
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with cluster points
 */
export const useGeneratorsClusters = (
  params: ClusterParams,
  enabled = true
) => {
  return useQuery<ClusterPoint[], Error>({
    queryKey: generatorsKeys.clusters(params),
    queryFn: () => generatorsService.getGeneratorsClusters(params),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to search generators
 *
 * @param query - Search query string
 * @param params - Additional query parameters (status)
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with matching generators
 */
export const useSearchGenerators = (
  query: string,
  params?: GeneratorMapParams,
  enabled = true
) => {
  return useQuery<GeneratorMapPoint[], Error>({
    queryKey: generatorsKeys.search(query, params),
    queryFn: () => generatorsService.searchGenerators(query, params),
    enabled: enabled && query.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
